import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.14, duration: 1.05, ease: EASE },
  }),
};

export default function Hero({ onExplore, onRandom }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="pointer-events-none relative flex min-h-screen items-center"
    >
      <div
        aria-hidden
        data-testid="hero-scrim"
        className="absolute inset-y-0 left-0 w-full bg-[radial-gradient(ellipse_90%_70%_at_18%_50%,rgba(11,12,16,0.94)_0%,rgba(11,12,16,0.78)_38%,rgba(11,12,16,0)_72%)] lg:w-[78%]"
      />
      <motion.div style={{ y, opacity }} className="relative w-full px-5 pb-24 pt-28 lg:px-16">
        <div className="max-w-[760px]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF9F1C] opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF9F1C]" />
            </span>
            <span className="font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]">
              CHRONO-ATLAS · N°01 — THE QUESTIONED EARTH
            </span>
          </motion.div>

          <h1 className="font-serif text-[13vw] leading-[0.98] [text-shadow:0_2px_28px_rgba(11,12,16,0.95),0_0_6px_rgba(11,12,16,0.8)] sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="block">
                Every stone
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={1} variants={lineVariants} initial="hidden" animate="show" className="block">
                keeps a <em className="italic text-[#D4AF37]">secret.</em>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={2}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block font-light italic text-white/60"
              >
                Or two.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
            className="mt-7 max-w-[520px] text-sm font-light leading-relaxed text-white/65 [text-shadow:0_1px_12px_rgba(11,12,16,0.9)] sm:text-base"
          >
            Fifteen monuments. Forty-five ways the story could have gone. An
            interactive globe of the sites where the official narrative is
            still — delightfully — negotiable.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="pointer-events-auto mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-button"
              onClick={onExplore}
              className="group relative overflow-hidden rounded-full bg-[#D4AF37] px-8 py-3.5 font-mono text-[11px] tracking-[0.28em] text-[#0B0C10] transition-colors duration-300 hover:bg-[#FF9F1C]"
            >
              BEGIN THE EXPEDITION
            </button>
            <button
              data-testid="hero-random-button"
              onClick={onRandom}
              className="rounded-full border border-white/25 px-8 py-3.5 font-mono text-[11px] tracking-[0.28em] text-white/80 transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              FLY TO A RANDOM SITE
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute inset-x-0 bottom-8 flex items-end justify-between px-5 lg:px-16"
      >
        <button
          data-testid="hero-scroll-hint"
          onClick={onExplore}
          className="pointer-events-auto flex items-center gap-3 font-mono text-[9px] tracking-[0.35em] text-white/40 transition-colors hover:text-[#D4AF37]"
        >
          <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
          SCROLL — THE PREMISE
        </button>
        <span className="hidden font-mono text-[9px] tracking-[0.35em] text-white/40 sm:block">
          DRAG THE GLOBE · CLICK A BEACON
        </span>
      </motion.div>
    </section>
  );
}
